import { Helmet } from 'react-helmet-async';

const COLORS = { bg: "#0a0a0f", text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0", border: "#1e1e2e", card: "#13131a" };

const POSTS = [
  {
    href: '/blog/how-to-pay-off-credit-card-debt/',
    title: 'How to Pay Off Credit Card Debt: A Step-by-Step Guide',
    desc: 'A practical plan to eliminate credit card debt — from listing what you owe to choosing a payoff strategy and sticking with it.',
    tag: 'Credit Cards',
  },
  {
    href: '/blog/how-to-get-out-of-debt-fast/',
    title: 'How to Get Out of Debt Fast (Even on a Tight Budget)',
    desc: 'Proven tactics to accelerate debt payoff: the debt avalanche, the snowball, balance transfers, and finding extra cash.',
    tag: 'Strategy',
  },
  {
    href: '/blog/pay-off-debt-or-invest/',
    title: 'Pay Off Debt or Invest? How to Decide',
    desc: 'The interest rate threshold, emergency fund rule, and employer match logic that determine the right call for your situation.',
    tag: 'Strategy',
  },
  {
    href: '/blog/how-long-to-pay-off-debt/',
    title: 'How Long Does It Take to Pay Off Debt?',
    desc: 'What the math actually shows — and how minimum payments quietly extend your timeline by years.',
    tag: 'Education',
  },
  {
    href: '/blog/debt-consolidation-pros-cons/',
    title: 'Debt Consolidation: Pros, Cons and When It Makes Sense',
    desc: 'Balance transfer cards, personal loans, and debt management plans compared — with the numbers that matter.',
    tag: 'Education',
  },
  {
    href: '/blog/debt-snowball-vs-avalanche/',
    title: 'Debt Snowball vs Avalanche: Which Strategy Wins?',
    desc: 'A side-by-side comparison of both methods with real examples — so you can pick the one that fits how you actually behave.',
    tag: 'Strategy',
  },
  {
    href: '/blog/pay-off-debt-low-income/',
    title: 'How to Pay Off Debt on a Low Income',
    desc: 'Realistic steps for paying down debt when money is tight — budgeting hacks, hardship programs, and prioritisation.',
    tag: 'Strategy',
  },
  {
    href: '/blog/average-credit-card-debt-canada/',
    title: 'Average Credit Card Debt in Canada (2025)',
    desc: 'What Canadians owe on credit cards, how it compares by province, and what to do if your balance is above average.',
    tag: 'Data',
  },
  {
    href: '/blog/pay-off-10000-credit-card-debt-uk/',
    title: 'How to Pay Off £10,000 Credit Card Debt in the UK',
    desc: 'A month-by-month plan for clearing £10k of credit card debt — with realistic timelines and interest cost breakdowns.',
    tag: 'UK',
  },
];

export default function BlogIndex() {
  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: COLORS.text }}>
      <Helmet>
        <title>Debt Payoff Blog — Tips, Strategies & Guides | ClearMyDebt</title>
        <meta name="description" content="Practical guides on paying off credit card debt, student loans, and personal loans. Snowball vs avalanche, debt consolidation, and more." />
        <link rel="canonical" href="https://clearmydebt.app/blog/" />
        <meta property="og:title" content="Debt Payoff Blog — ClearMyDebt" />
        <meta property="og:description" content="Practical guides on paying off debt fast. Strategies, comparisons, and real numbers." />
        <meta property="og:url" content="https://clearmydebt.app/blog/" />
        <meta property="og:image" content="https://clearmydebt.app/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://clearmydebt.app/og-image.png" />
      </Helmet>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 80px' }}>
        <div style={{ fontSize: '11px', letterSpacing: '3px', color: COLORS.accent, fontWeight: 700, marginBottom: '16px' }}>
          <a href="/" style={{ color: COLORS.accent, textDecoration: 'none' }}>ClearMyDebt</a>
          <span style={{ margin: '0 8px', opacity: 0.4 }}>›</span>
          Blog
        </div>

        <h1 style={{ fontSize: 'clamp(26px,5vw,40px)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px', background: `linear-gradient(135deg,#fff,${COLORS.accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Debt Payoff Guides
        </h1>
        <p style={{ color: COLORS.muted, fontSize: '1.05rem', marginBottom: 48, lineHeight: 1.6 }}>
          Practical strategies and real numbers to help you get out of debt faster.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {POSTS.map(post => (
            <a
              key={post.href}
              href={post.href}
              style={{ display: 'block', background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '20px 24px', textDecoration: 'none', transition: 'border-color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent}
              onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: COLORS.accent, background: 'rgba(0,229,160,0.1)', padding: '3px 8px', borderRadius: 4 }}>
                  {post.tag}
                </span>
              </div>
              <h2 style={{ color: COLORS.text, fontSize: '1.05rem', fontWeight: 700, margin: '0 0 6px', lineHeight: 1.4 }}>
                {post.title}
              </h2>
              <p style={{ color: COLORS.muted, fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
                {post.desc}
              </p>
            </a>
          ))}
        </div>

        <div style={{ marginTop: 64, paddingTop: 32, borderTop: `1px solid ${COLORS.border}` }}>
          <p style={{ color: COLORS.muted, fontSize: '0.85rem', marginBottom: 12 }}>Ready to make a plan?</p>
          <a href="/" style={{ display: 'inline-block', background: COLORS.accent, color: '#000', fontWeight: 800, fontSize: '0.95rem', padding: '12px 24px', borderRadius: 8, textDecoration: 'none' }}>
            Try the Free Debt Calculator →
          </a>
        </div>
      </div>
    </div>
  );
}
